/**
 * Sticky Cart Module - 優化版
 * 處理購物車的即時更新、顯示/隱藏、數量調整等功能
 */

// 狀態管理和常量
let isCartLoading = false;
let lastDeviceType = null; // 追蹤上一次的設備類型
const BREAKPOINTS = {
  mobile: 780,
  tablet: 1440
};

// 工具函數
const isMobile = () => window.innerWidth <= BREAKPOINTS.mobile;
const isTablet = () => window.innerWidth >= BREAKPOINTS.mobile && window.innerWidth < BREAKPOINTS.tablet;
const isDesktop = () => window.innerWidth >= BREAKPOINTS.tablet;
const getDevice = () => isDesktop() ? 'desktop' : (isTablet() ? 'tablet' : 'mobile');

// DOM 選擇器緩存
const getElements = () => ({
  cart: document.querySelector(".sticky-cart"),
  wrapper: document.querySelector(".sticky-cart-wrapper"),
  button: document.getElementById("open-cart-button"),
  closeButton: document.getElementById("close-cart-button"),
  cartBox: document.querySelector(".sticky-cart-box"),
  productWrapper: document.querySelector('.collection-product-wrapper'),
  header: document.querySelector('header') || document.querySelector('.header'),
  loadingEl: document.querySelector(".cart-loading-animation")
});

/**
 * 檢查設備類型變化並調整顯示狀態
 */
function checkDeviceTypeChange() {
  const currentDevice = getDevice();
  
  // 如果設備類型沒有改變，不需要做任何事
  if (lastDeviceType === currentDevice) {
    return;
  }
  
  const { cart, button, wrapper } = getElements();
  if (!cart || !button || !wrapper) {
    return;
  }
  
  // 根據新的設備類型重新設置顯示狀態
  if (currentDevice === 'desktop') {
    wrapper.style.display = "block";
    cart.style.display = "flex";
    button.style.display = "none";
    wrapper.classList.remove("show");
    document.body.style.overflow = "";
  } else if (currentDevice === 'tablet') {
    wrapper.style.display = "block";
    cart.style.display = "none";
    wrapper.classList.remove("show");
    // 確保按鈕顯示並應用正確樣式
    button.style.display = "block";
    button.style.visibility = "visible";
    button.style.opacity = "1";
    applyTabletButtonStyles(button);
    document.body.style.overflow = "";
  } else if (currentDevice === 'mobile') {
    wrapper.style.display = "block";
    cart.style.display = "none";
    wrapper.classList.remove("show");
    // 確保按鈕顯示並應用正確樣式
    button.style.display = "block";
    button.style.visibility = "visible";
    button.style.opacity = "1";
    applyMobileButtonStyles(button);
    document.body.style.overflow = "";
  }
  
  // 更新上一次的設備類型
  lastDeviceType = currentDevice;
}
function toggleCartLoading(show) {
  isCartLoading = show;
  const { loadingEl } = getElements();
  if (loadingEl) loadingEl.style.display = show ? "block" : "none";
}

/**
 * 更新購物車計數顯示
 */
function updateCartItemCounts(count) {
  document.querySelectorAll(".cart-count").forEach(el => el.textContent = count);
  document.dispatchEvent(new Event("cart:update"));
}

/**
 * 獲取當前頁面的 section ID
 */
function getSectionId() {
  const path = window.location.pathname;
  if (path.includes('/search')) return 'template-search';
  if (path.includes('/products/')) return 'template-product';
  return 'template-collection';
}

/**
 * 獲取購物車內容
 */
async function fetchCartContent(sectionId) {
  try {
    const res = await fetch(`${window.location.pathname}?section_id=${sectionId}&t=${Date.now()}`);
    if (res.ok) return res;
    
    return await fetch(`/?section_id=${sectionId}&t=${Date.now()}`);
  } catch (error) {
    throw new Error(`Failed to fetch cart content: ${error.message}`);
  }
}

/**
 * 更新購物車內容
 */
async function updateCartDrawer() {
  if (isCartLoading) return;
  toggleCartLoading(true);
  
  try {
    // 更新購物車計數
    const cartRes = await fetch("/cart.json");
    if (cartRes.ok) {
      const cart = await cartRes.json();
      updateCartItemCounts(cart.item_count);
    }
    
    // 獲取更新的購物車內容
    const sectionId = getSectionId();
    const res = await fetchCartContent(sectionId);
    
    if (res.ok) {
      await updateCartContent(await res.text());
    }
  } catch (error) {
    console.error("Error updating cart:", error);
  } finally {
    toggleCartLoading(false);
  }
}

/**
 * 更新購物車 DOM 內容
 */
async function updateCartContent(htmlText) {
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = htmlText;
  
  const newCart = tempDiv.querySelector(".sticky-cart");
  const { cart: currentCart } = getElements();
  
  if (newCart && currentCart) {
    const isVisible = currentCart.style.display !== 'none';
    currentCart.innerHTML = newCart.innerHTML;
    
    if (!isVisible) currentCart.style.display = 'none';
    
    // 強制重排以確保價格顯示
    setTimeout(() => {
      const { wrapper } = getElements();
      if (wrapper) {
        wrapper.style.height = 'auto';
        wrapper.offsetHeight; // 觸發 reflow
      }
      
      // 重新初始化滾動增強功能，因為 DOM 內容已更新
      startScrollEnhancements();
    }, 50);
    
    initializeStickyCartListeners();
    
    // 重新初始化表單（如果存在）
    if (window.initializeAddToCartForms) {
      window.initializeAddToCartForms();
    }
  }
}

/**
 * 數量調整事件監聽器
 */
function addQuantityListeners() {
  // 移除舊的事件監聽器
  document.querySelectorAll(".sticky-cart-quantity-selector button[data-listener-added]")
    .forEach(button => {
      button.removeAttribute('data-listener-added');
      const newButton = button.cloneNode(true);
      button.parentNode.replaceChild(newButton, button);
    });

  document.querySelectorAll(".sticky-cart-quantity-selector button")
    .forEach(button => {
      if (button.hasAttribute('data-listener-added')) return;
      
      button.addEventListener("click", async () => {
        try {
          const item = button.closest(".sticky-cart-item");
          const key = item.getAttribute("data-line-item-key");
          const input = button.parentElement.querySelector("input");
          const currentQty = Number(input.value);
          const isIncrease = button.classList.contains("sticky-cart-quantity-selector-plus");
          const newQty = isIncrease ? currentQty + 1 : currentQty - 1;

          const res = await fetch("/cart/change.js", {
            method: "POST",
            headers: {
              "Accept": "application/json",
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ id: key, quantity: newQty }),
          });

          if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);

          const cart = await res.json();
          updateCartItemCounts(cart.item_count);
          await updateCartDrawer();
        } catch (error) {
          console.error("Error updating quantity:", error);
          toggleCartLoading(false);
        }
      });
      
      button.setAttribute('data-listener-added', 'true');
    });
}

/**
 * 購物車顯示/隱藏控制
 */
function toggleCartVisibility(show, device = 'mobile') {
  const { cart, wrapper, button } = getElements();
  if (!cart || !wrapper || !button) return;

  if (show) {
    // 顯示購物車
    if (device === 'tablet') {
      wrapper.style.display = "block";
      cart.style.display = "block";
      wrapper.classList.add("show");
      button.style.display = "none";
    } else if (device === 'mobile') {
      wrapper.style.display = "block";
      cart.style.display = "block";
      wrapper.classList.add("show");  // 使用 show 類別控制動畫
      button.style.display = "none";
      
      // 防止背景滾動 - 手機版專用
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.width = "100%";
      document.body.style.top = `-${window.scrollY}px`;
    }
    
    // 平板版也防止背景滾動
    if (device === 'tablet') {
      document.body.style.overflow = "hidden";
    }
  } else {
    // 隱藏購物車 - 立即顯示按鈕，避免閃爍
    if (device === 'tablet') {
      wrapper.classList.remove("show");
      // 立即顯示按鈕，確保不會消失
      button.style.display = "block";
      button.style.visibility = "visible";
      button.style.opacity = "1";
      applyTabletButtonStyles(button);
      
      setTimeout(() => {
        cart.style.display = "none";
        // wrapper保持顯示但在螢幕外
      }, 300);
    } else if (device === 'mobile') {
      wrapper.classList.remove("show");  // 移除 show 類別觸發隱藏動畫
      // 立即顯示按鈕，確保不會消失
      button.style.display = "block";
      button.style.visibility = "visible";
      button.style.opacity = "1";
      applyMobileButtonStyles(button);
      
      setTimeout(() => {
        cart.style.display = "none";
        wrapper.style.display = "block";  // wrapper 保持可見（為了按鈕顯示）
        // 再次確保按鈕可見
        button.style.display = "block";
        button.style.visibility = "visible";
        button.style.opacity = "1";
      }, 300);
      
      // 恢復背景滾動 - 手機版專用
      const scrollY = document.body.style.top;
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
      document.body.style.top = "";
      
      // 恢復滾動位置
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    }
    
    // 平板版恢復背景滾動
    if (device === 'tablet') {
      document.body.style.overflow = "";
    }
  }
}

/**
 * 按鈕事件監聽器
 */
function attachButtonListeners() {
  const { button: openBtn, closeButton: closeBtn, productWrapper } = getElements();
  if (!openBtn || !closeBtn) return;
  
  // 移除舊的事件監聽器（如果有的話）
  const clickHandler = openBtn._clickHandler;
  if (clickHandler) {
    openBtn.removeEventListener('click', clickHandler);
  }
  
  // 確保按鈕在手機/平板版可見
  ensureButtonVisible();
  
  // 開啟購物車
  const newClickHandler = () => {
    if (isDesktop()) {
      // 桌面版：滾動到產品區域
      if (productWrapper) {
        productWrapper.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    } else {
      toggleCartVisibility(true, getDevice());
    }
  };
  
  openBtn.addEventListener("click", newClickHandler);
  openBtn._clickHandler = newClickHandler;

  // 關閉購物車（只有非桌面版）
  closeBtn.addEventListener("click", () => {
    if (!isDesktop()) {
      toggleCartVisibility(false, getDevice());
      // 關閉後確保按鈕仍然可見
      setTimeout(() => {
        ensureButtonVisible();
      }, 350);
    }
  });
}

/**
 * 應用平板版按鈕樣式
 */
function applyTabletButtonStyles(button) {
  if (!button) return;
  
  const { wrapper } = getElements();
  const isCartOpen = wrapper && wrapper.classList.contains('show');
  
  // 確保有正確的類別
  if (!button.classList.contains('open-cart-button')) {
    button.classList.add('open-cart-button');
  }
  
  // 只在購物車關閉時設置顯示樣式
  if (!isCartOpen) {
    button.style.display = 'block';
    button.style.visibility = 'visible';
    button.style.opacity = '1';
  }
  
  button.style.pointerEvents = 'auto';
}

/**
 * 應用手機版按鈕樣式
 */
function applyMobileButtonStyles(button) {
  if (!button) return;
  
  const { wrapper } = getElements();
  const isCartOpen = wrapper && wrapper.classList.contains('show');
  
  // 確保有正確的類別
  if (!button.classList.contains('open-cart-button')) {
    button.classList.add('open-cart-button');
  }
  
  // 只在購物車關閉時設置顯示樣式
  if (!isCartOpen) {
    button.style.display = 'flex'; // 手機版使用 flex 來居中對齊
    button.style.visibility = 'visible';
    button.style.opacity = '1';
  }
  
  button.style.pointerEvents = 'auto';
}

/**
 * 外部點擊關閉購物車
 */
function addOutsideClickListeners() {
  if (eventListenersAdded.outside) return;
  
  const handleOutsideClick = (event) => {
    if (isDesktop()) return;
    
    const { cart, button, wrapper } = getElements();
    
    if (cart?.style.display === 'block' && 
        !cart.contains(event.target) && 
        !button?.contains(event.target)) {
      
      toggleCartVisibility(false, getDevice());
      // 外部點擊關閉後確保按鈕仍然可見
      setTimeout(() => {
        ensureButtonVisible();
      }, 350);
    }
  };

  ['click', 'touchstart'].forEach(eventType => {
    document.addEventListener(eventType, handleOutsideClick, { passive: true });
  });
  
  eventListenersAdded.outside = true;
}

/**
 * 觸控事件處理（防止冒泡）+ 拖動條關閉手勢
 */
function addTouchListeners() {
  const { wrapper, cart } = getElements();
  if (!wrapper || !cart) return;

  let startY = 0;
  let currentY = 0;
  let isDragging = false;
  let startedInDragZone = false;

  // 拖動條關閉手勢（僅限手機版）
  if (isMobile()) {
    const handleTouchStart = (e) => {
      if (!cart.contains(e.target)) return;
      
      // 檢查是否點擊在拖動條區域（購物車頂部 40px 區域）
      const cartRect = cart.getBoundingClientRect();
      const relativeY = e.touches[0].clientY - cartRect.top;
      
      startY = e.touches[0].clientY;
      isDragging = false;
      
      // 標記是否從拖動區域開始
      startedInDragZone = relativeY <= 40;
    };

    const handleTouchMove = (e) => {
      if (startY === 0) return;
      
      // 如果不是從拖動區域開始的觸摸，完全不處理
      if (!startedInDragZone) {
        return;
      }
      
      currentY = e.touches[0].clientY;
      const deltaY = currentY - startY;
      
      // 只處理向下拖動的情況，且必須從拖動區域開始
      if (deltaY > 10) {
        isDragging = true;
        e.preventDefault();
        
        // 視覺反饋：輕微向下移動購物車
        const translateY = Math.min(deltaY * 0.5, 50);
        cart.style.transform = `translateY(${translateY}px)`;
        cart.style.opacity = Math.max(1 - deltaY / 200, 0.7);
      }
    };

    const handleTouchEnd = (e) => {
      const deltaY = currentY - startY;
      
      if (isDragging && startedInDragZone) {
        // 重置下滑關閉的樣式
        cart.style.transform = '';
        cart.style.opacity = '';
        
        // 如果下滑距離超過 80px，關閉購物車
        if (deltaY > 80) {
          toggleCartVisibility(false, 'mobile');
          // 確保關閉後按鈕仍可見
          setTimeout(() => {
            ensureButtonVisible();
          }, 350);
        }
        
        isDragging = false;
      }
      
      startY = 0;
      startedInDragZone = false;
    };

    // 只在購物車本身添加觸控事件，不在 wrapper 上添加
    cart.addEventListener('touchstart', handleTouchStart, { passive: false });
    cart.addEventListener('touchmove', handleTouchMove, { passive: false });
    cart.addEventListener('touchend', handleTouchEnd, { passive: true });
  }
}

/**
 * 桌面版位置更新
 */
function updateDesktopPosition(wrapper, button) {
  // 嚴格確保只在桌面版執行，避免影響其他設備
  if (!isDesktop()) {
    return;
  }
  
  const { productWrapper, header } = getElements();
  if (!productWrapper) return;

  const wrapperRect = productWrapper.getBoundingClientRect();
  const windowHeight = window.innerHeight;
  const { top: wrapperTop, bottom: wrapperBottom } = wrapperRect;
  
  const isWrapperVisible = wrapperBottom > 0 && wrapperTop < windowHeight;

  if (isWrapperVisible) {
    // 設置基本樣式
    Object.assign(wrapper.style, {
      display: 'block',
      height: 'auto',
      maxHeight: 'none',
      overflow: 'visible',
      transform: 'none'
    });
    button.style.display = 'none';
    
    const actualHeight = wrapper.offsetHeight;
    const headerHeight = header ? header.offsetHeight : 0;
    const minTopPosition = headerHeight + 10;
    
    let finalTop;
    
    // 計算位置
    if (wrapperTop > minTopPosition) {
      finalTop = wrapperTop + 20;
    } else if (wrapperBottom < windowHeight - 20) {
      finalTop = wrapperBottom - actualHeight - 20;
    } else {
      const centerTop = (windowHeight - actualHeight) / 2;
      const maxAllowedTop = wrapperBottom - actualHeight - 20;
      const minAllowedTop = Math.max(wrapperTop + 20, minTopPosition);
      
      if (wrapperTop <= minTopPosition && wrapperBottom >= windowHeight) {
        finalTop = Math.max(minTopPosition, centerTop);
      } else if (wrapperTop <= minTopPosition) {
        finalTop = Math.max(Math.min(centerTop, maxAllowedTop), minTopPosition);
      } else {
        finalTop = Math.max(centerTop, minAllowedTop);
      }
    }
    
    wrapper.style.top = finalTop + 'px';
  } else {
    wrapper.style.display = 'none';
    button.style.display = 'block';
  }
}

/**
 * 移動版位置更新 - 簡化版本
 */
function updateMobilePosition(button) {
  // 嚴格確保只在手機版執行，避免影響其他設備
  if (!isMobile()) {
    return;
  }
  
  const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
  const windowHeight = window.innerHeight;
  const documentHeight = document.documentElement.scrollHeight;
  const distanceFromBottom = documentHeight - scrollTop - windowHeight;
  
  let bottomOffset = 20;
  if (distanceFromBottom < 150) {
    bottomOffset = Math.max(20, 150 - distanceFromBottom + 20);
  }
  
  const { cart } = getElements();
  if (!cart || cart.style.display !== 'block') {
    button.style.bottom = bottomOffset + 'px';
    // 只設置位置，不覆蓋其他樣式
    applyMobileButtonStyles(button);
  }
}

/**
 * 強制確保手機/平板版按鈕可見（修復按鈕消失問題）
 */
function ensureButtonVisible() {
  const { button, wrapper } = getElements();
  if (!button || !wrapper) return;
  
  const currentDevice = getDevice();
  const isCartOpen = wrapper.classList.contains('show');
  
  // 只在購物車關閉時確保按鈕可見
  if (!isCartOpen && (currentDevice === 'mobile' || currentDevice === 'tablet')) {
    // 確保 wrapper 顯示（但可能不在螢幕上）
    wrapper.style.display = "block";
    
    // 應用對應的樣式（會設置所有必要的可見性屬性）
    if (currentDevice === 'mobile') {
      applyMobileButtonStyles(button);
    } else {
      applyTabletButtonStyles(button);
    }
  }
}

/**
 * 事件監聽器狀態管理
 */
let eventListenersAdded = {
  scroll: false,
  resize: false,
  orientationchange: false,
  outside: false
};

/**
 * 滾動跟隨功能
 */
function addScrollFollowListener() {
  if (eventListenersAdded.scroll) return;
  
  let isScrolling = false;
  let scrollTimeout;

  function updateCartPosition() {
    const { wrapper: cartWrapper, button: openButton, cart } = getElements();
    if (!cartWrapper || !openButton || !cart) return;

    const currentDevice = getDevice();
    const isCartOpen = cartWrapper.classList.contains('show');
    
    // 只在購物車關閉時確保按鈕狀態正確
    if (!isCartOpen) {
      if (currentDevice === 'mobile') {
        // 手機版購物車關閉時確保按鈕可見
        applyMobileButtonStyles(openButton);
      } else if (currentDevice === 'tablet') {
        // 平板版購物車關閉時確保按鈕可見
        applyTabletButtonStyles(openButton);
      }
    }

    if (isDesktop()) {
      updateDesktopPosition(cartWrapper, openButton);
    } else if (isMobile()) {
      updateMobilePosition(openButton);
    }
    // 平板版不需要特殊的位置更新邏輯
  }

  function handleScroll() {
    if (!isScrolling) {
      isScrolling = true;
      requestAnimationFrame(() => {
        updateCartPosition();
        isScrolling = false;
      });
    }
    
    clearTimeout(scrollTimeout);
    scrollTimeout = setTimeout(updateCartPosition, 150);
  }

  function handleResize() {
    clearTimeout(scrollTimeout);
    scrollTimeout = setTimeout(() => {
      checkDeviceTypeChange();
      updateCartPosition();
    }, 200);
  }

  function handleOrientationChange() {
    checkDeviceTypeChange();
    updateCartPosition();
  }

  // 事件監聽器 - 統一管理
  if (!eventListenersAdded.scroll) {
    window.addEventListener('scroll', handleScroll, { passive: true });
    eventListenersAdded.scroll = true;
  }
  
  if (!eventListenersAdded.resize) {
    window.addEventListener('resize', handleResize, { passive: true });
    eventListenersAdded.resize = true;
  }
  
  if (!eventListenersAdded.orientationchange) {
    window.addEventListener('orientationchange', handleOrientationChange, { passive: true });
    eventListenersAdded.orientationchange = true;
  }

  window.updateCartPosition = updateCartPosition;
}

/**
 * 初始化所有事件監聽器
 */
function initializeStickyCartListeners() {
  addQuantityListeners();
  attachButtonListeners();
  
  // 防止購物車內容區域點擊冒泡
  const { cartBox } = getElements();
  if (cartBox) {
    cartBox.addEventListener("click", e => e.stopPropagation());
  }
}

/**
 * 初始化 Sticky Cart 模組
 */
function initializeStickyCart() {
  const { cart, button, wrapper } = getElements();
  
  // 設置初始設備類型
  lastDeviceType = getDevice();
  
  // 完全由JavaScript控制顯示/隱藏狀態
  if (isDesktop() && cart && button && wrapper) {
    // 桌面版：顯示購物車和wrapper，隱藏按鈕
    wrapper.style.display = "block";
    cart.style.display = "flex";
    button.style.display = "none";
  } else if (isTablet() && cart && button && wrapper) {
    // 平板版：隱藏購物車，顯示按鈕，wrapper準備待命
    wrapper.style.display = "block";
    cart.style.display = "none";
    wrapper.classList.remove("show");
    button.style.display = "block";
    button.style.visibility = "visible";
    button.style.opacity = "1";
    
    // 應用平板版專用樣式
    applyTabletButtonStyles(button);
  } else if (isMobile() && cart && button && wrapper) {
    // 手機版：顯示 wrapper（讓按鈕可見），隱藏購物車，顯示按鈕
    wrapper.style.display = "block";  // 🔧 修正：手機版 wrapper 必須 display: block
    cart.style.display = "none";
    wrapper.classList.remove("show");
    button.style.display = "block";
    button.style.visibility = "visible";
    button.style.opacity = "1";
    
    // 應用手機版專用樣式
    applyMobileButtonStyles(button);
    
    // 清理舊的購物車高度設置
    cleanupOldCartSettings();
    
    // 額外保障：確保按鈕在 DOM 中且可見
    setTimeout(() => {
      if (button && isMobile()) {
        button.style.display = "block";
        button.style.visibility = "visible";
        button.style.opacity = "1";
        applyMobileButtonStyles(button);
      }
    }, 100);
  }
  
  initializeStickyCartListeners();
  addOutsideClickListeners();
  addTouchListeners();
  addScrollFollowListener();
  
  // 購物車高度調整功能已移除，僅保留向下拖動關閉功能
  
  // 初始化滾動增強功能
  setTimeout(() => {
    startScrollEnhancements();
  }, 200); // 稍微延遲以確保 DOM 完全準備好
}

// 自動初始化 - 優化重複調用
let isInitialized = false;

function performInitialization() {
  if (isInitialized) return;
  
  initializeStickyCart();
  isInitialized = true;
}

document.addEventListener('DOMContentLoaded', performInitialization);
window.addEventListener('load', performInitialization);

// 導出 API
window.StickyCart = {
  updateCartDrawer,
  updateCartItemCounts,
  showCartLoading: () => toggleCartLoading(true),
  hideCartLoading: () => toggleCartLoading(false),
  initializeStickyCartListeners,
  initializeStickyCart,
  applyTabletButtonStyles,
  applyMobileButtonStyles,
  checkDeviceTypeChange,
  ensureButtonVisible,
  forceRefresh: () => {
    // 強制重新檢查和設置
    lastDeviceType = null;
    checkDeviceTypeChange();
    ensureButtonVisible();
  }
};

/**
 * 清理舊的購物車高度設置
 */
function cleanupOldCartSettings() {
  // 移除舊的高度設置
  localStorage.removeItem('sticky-cart-height');
  
  // 移除任何可能存在的 CSS 變數
  document.documentElement.style.removeProperty('--cart-height');
  
  // 重置購物車內聯樣式
  const { cart } = getElements();
  if (cart && isMobile()) {
    cart.style.height = '';
    cart.style.transition = '';
  }
}

// 購物車高度恢復功能已移除
// 現在手機版購物車使用固定的 CSS 高度設置

/**
 * 重置購物車高度到預設值
 */
// 多次點擊拖拽條調整高度功能已移除
// 現在手機版購物車只支援向下拖動關閉功能

/**
 * 滾动狀態檢測和視覺提示功能
 */
function initScrollEnhancements() {
  const cartItems = document.querySelector('.sticky-cart-items');
  if (!cartItems) {
    console.warn('Sticky cart items container not found. Scroll enhancements disabled.');
    return null;
  }

  // 檢查是否有滾動內容
  function checkScrollableContent() {
    const hasOverflow = cartItems.scrollHeight > cartItems.clientHeight;
    const itemCount = cartItems.querySelectorAll('.product-info').length;
    
    // 添加對應的 CSS 類別
    cartItems.classList.toggle('has-scroll', hasOverflow);
    cartItems.classList.toggle('has-many-items', itemCount > 3);
    
    return hasOverflow;
  }

  // 檢測滾動位置並添加相應的陰影效果
  function updateScrollShadows() {
    const scrollTop = cartItems.scrollTop;
    const scrollHeight = cartItems.scrollHeight;
    const clientHeight = cartItems.clientHeight;
    const scrollBottom = scrollHeight - scrollTop - clientHeight;
    
    // 根據滾動位置添加/移除 CSS 類別
    cartItems.classList.toggle('scroll-top', scrollTop > 10);
    cartItems.classList.toggle('scroll-bottom', scrollBottom > 10);
  }

  // 滾動中的視覺反饋（手機版）
  let scrollTimer;
  function handleScrollFeedback() {
    if (isMobile()) {
      cartItems.classList.add('scrolling');
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(() => {
        cartItems.classList.remove('scrolling');
      }, 150);
    }
  }

  // 初始檢查
  setTimeout(() => {
    checkScrollableContent();
    updateScrollShadows();
  }, 100);

  // 監聽滾動事件
  cartItems.addEventListener('scroll', () => {
    updateScrollShadows();
    handleScrollFeedback();
  }, { passive: true });

  // 監聽內容變化（商品增減）
  const resizeObserver = new ResizeObserver(() => {
    setTimeout(() => {
      checkScrollableContent();
      updateScrollShadows();
    }, 50);
  });
  
  resizeObserver.observe(cartItems);

  // 監聽 DOM 變化（商品增減）
  const mutationObserver = new MutationObserver(() => {
    setTimeout(() => {
      checkScrollableContent();
      updateScrollShadows();
    }, 100);
  });
  
  mutationObserver.observe(cartItems, {
    childList: true,
    subtree: true
  });

  // 窗口大小變化時重新檢查
  const handleResize = () => {
    setTimeout(() => {
      checkScrollableContent();
      updateScrollShadows();
    }, 200);
  };
  
  window.addEventListener('resize', handleResize, { passive: true });

  return {
    checkScrollableContent,
    updateScrollShadows,
    destroy: () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      window.removeEventListener('resize', handleResize);
    }
  };
}

/**
 * 平滑滾動到購物車中的特定商品
 */
function scrollToCartItem(productId) {
  const cartItems = document.querySelector('.sticky-cart-items');
  const targetItem = cartItems?.querySelector(`[data-product-id="${productId}"]`);
  
  if (targetItem && cartItems) {
    const itemTop = targetItem.offsetTop;
    const containerHeight = cartItems.clientHeight;
    const itemHeight = targetItem.offsetHeight;
    const scrollTop = itemTop - (containerHeight / 2) + (itemHeight / 2);
    
    cartItems.scrollTo({
      top: Math.max(0, scrollTop),
      behavior: 'smooth'
    });
  }
}

/**
 * 購物車滾動到頂部/底部的便利函數
 */
function scrollCartToTop() {
  const cartItems = document.querySelector('.sticky-cart-items');
  if (cartItems) {
    cartItems.scrollTo({ top: 0, behavior: 'smooth' });
  }
}

function scrollCartToBottom() {
  const cartItems = document.querySelector('.sticky-cart-items');
  if (cartItems) {
    cartItems.scrollTo({ top: cartItems.scrollHeight, behavior: 'smooth' });
  }
}

// 全域滾动增強實例
let scrollEnhancementInstance = null;

// 在購物車初始化時啟動滾动增強功能
function startScrollEnhancements() {
  if (scrollEnhancementInstance) {
    scrollEnhancementInstance.destroy();
    scrollEnhancementInstance = null;
  }
  
  // 嘗試多次初始化滾動增強功能，以防 DOM 還未完全準備好
  let attempts = 0;
  const maxAttempts = 5;
  
  function tryInitialize() {
    attempts++;
    const instance = initScrollEnhancements();
    
    if (instance) {
      scrollEnhancementInstance = instance;
      console.log('Scroll enhancements initialized successfully');
    } else if (attempts < maxAttempts) {
      console.log(`Scroll enhancements initialization attempt ${attempts} failed, retrying...`);
      setTimeout(tryInitialize, 200 * attempts); // 遞增延遲
    } else {
      console.warn('Failed to initialize scroll enhancements after multiple attempts');
    }
  }
  
  tryInitialize();
}

// 清理滾动增強功能
function stopScrollEnhancements() {
  if (scrollEnhancementInstance) {
    scrollEnhancementInstance.destroy();
    scrollEnhancementInstance = null;
  }
}

// 將滾動增強功能暴露給全域作用域以便外部調用
window.stickyCartScrollUtils = {
  scrollToItem: scrollToCartItem,
  scrollToTop: scrollCartToTop,
  scrollToBottom: scrollCartToBottom,
  initEnhancements: startScrollEnhancements,
  stopEnhancements: stopScrollEnhancements
};


